// Exports the pivot model to .xlsx.
//
// The grid is a 2-D cross with multi-field row headers and a totals row/column,
// so `aoa_to_sheet` (array of arrays) is used instead of `json_to_sheet`:
// the sheet mirrors PivotGrid cell for cell.

import * as XLSX from "xlsx";
import { formatValue, VALUE_COUNT } from "./formatValue";

const SHEET_NAME_MAX = 31; // Excel hard limit

/**
 * Flattens the pivot model into the exact grid PivotGrid renders.
 * @param {Object} pivot - model from computePivot() / pivotFromAggregate()
 * @param {Object} labels - { fieldTitle, totalLabel, grandTotalLabel, countLabel, valueMode }
 *   countLabel heads the single value column; pass measureLabel() for a measure pivot.
 * @returns {Array<Array<string|number>>}
 */
export const pivotToAoA = (pivot, labels) => {
  const {
    fieldTitle,
    totalLabel,
    grandTotalLabel,
    countLabel,
    valueMode = VALUE_COUNT,
  } = labels;
  const {
    rowFields,
    rowKeys,
    colKeys,
    cells,
    rowTotals,
    colTotals,
    grandTotal,
    isAllRow,
    isAllCol,
  } = pivot;

  // Values share the grand total as their base so count and percent line up.
  // A measure pivot exports its days as plain numbers (blank = no data), so the
  // sheet stays calculable; the unit lives in countLabel.
  const fmt = (v) =>
    pivot.measure ? (v == null ? "" : v) : formatValue(v, grandTotal, valueMode);
  const at = (map, key) => map.get(key) ?? (pivot.measure ? null : 0);
  const cell = (rk, ck) => fmt(at(cells, `${rk}||${ck}`));
  const colHeader = (col) => (isAllCol ? countLabel : col.values.join(" ▸ "));

  const header = [
    ...(isAllRow ? [""] : rowFields.map(fieldTitle)),
    ...colKeys.map(colHeader),
    totalLabel,
  ];

  const body = rowKeys.map((row) => [
    ...(isAllRow ? [totalLabel] : row.values),
    ...colKeys.map((col) => cell(row.key, col.key)),
    fmt(at(rowTotals, row.key)),
  ]);

  // The grand-total label occupies the first header column; pad the rest so the
  // count columns stay aligned when there are several row fields.
  const rowHeaderSpan = isAllRow ? 1 : rowFields.length;
  const grand = [
    grandTotalLabel,
    ...Array(Math.max(0, rowHeaderSpan - 1)).fill(""),
    ...colKeys.map((col) => fmt(at(colTotals, col.key))),
    fmt(grandTotal),
  ];

  // With a column axis the measure name has no header cell of its own, so it
  // goes in a title row above the table.
  const title = pivot.measure && !isAllCol ? [[countLabel]] : [];

  return [...title, header, ...body, grand];
};

/** Column widths from the widest cell, clamped so long catalog paths don't explode. */
const columnWidths = (aoa) =>
  (aoa[0] || []).map((_, i) => {
    const width = aoa.reduce(
      (max, row) => Math.max(max, String(row[i] ?? "").length),
      0
    );
    return { wch: Math.min(40, Math.max(10, width + 2)) };
  });

/**
 * Builds and downloads the workbook.
 * @param {Object} pivot
 * @param {Object} opts - { fieldTitle, totalLabel, grandTotalLabel, countLabel,
 *                          sheetName, filename }
 */
export const exportPivotToExcel = (pivot, opts) => {
  if (!pivot) return;

  const aoa = pivotToAoA(pivot, opts);
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  worksheet["!cols"] = columnWidths(aoa);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    (opts.sheetName || "Pivot").slice(0, SHEET_NAME_MAX)
  );

  XLSX.writeFile(workbook, opts.filename);
};
